import { useCallback, useEffect, useState } from 'react';
import Icon from './Icon';
import Sheet from './Sheet';
import { useCapture } from './Capture';
import { useAiCredits } from '../lib/credits';
import { calorieSummary, proteinSummary } from '../lib/day';
import { today } from '../lib/dates';
import { MEAL_LABELS, grams, kcal } from '../lib/format';
import { SourceLine } from './DataSource';
import { useApp } from '../lib/AppContext';

// Đặt ngoài: when there's no time to cook a meal, three easy-to-find dishes close to what it needs, then
// GrabFood or ShopeeFood with the dish name already copied (neither app publishes a search link). Back in
// the app, "Bạn đã đặt món gì?" logs it in one tap. Suggestions are dish kinds, never restaurants.

const FIT_STYLE = { good: 'text-brand-text', over: 'text-[#c2410c]', low_protein: 'text-[#c2410c]', light: 'text-muted' };
const lower = (type) => MEAL_LABELS[type].toLowerCase();
const MAIN = ['breakfast', 'lunch', 'dinner'];

// What a slot needs when no planned dish says so: what's left of the day, shared over the main meals not
// logged yet from this one on (a snack gets a light share). Never less than a light meal.
export function slotTarget(day, mealType, calorieGoal) {
  const left = calorieSummary(day.totals, calorieGoal).left ?? 2000 - (day.totals?.calories_in ?? 0);
  const missing = proteinSummary(day.totals, day.protein_goal).missing ?? 0;
  const ahead = MAIN.slice(Math.max(0, MAIN.indexOf(mealType))).filter((t) => !day.meals.some((m) => m.meal_type === t));
  const share = mealType === 'snack' ? 4 : Math.max(1, ahead.length);
  return { calories: Math.max(300, Math.round(left / share / 10) * 10), protein: Math.max(15, Math.round(missing / share)) };
}

// The open "Bạn đã đặt món gì?" questions for today, and what answering them does. Reloads when the app comes
// back to the front (returning from GrabFood). `onPlan`/`onDay` receive the updated menu and day.
export function useTakeouts(date, { onPlan, onDay } = {}) {
  const { api, editable } = useApp();
  const capture = useCapture();
  const [open, setOpen] = useState([]);
  const active = editable && date === today();

  const load = useCallback(() => {
    if (!active) return setOpen([]);
    return api.get(`/takeouts?date=${date}`).then(setOpen).catch(() => {});
  }, [api, active, date]);
  useEffect(() => {
    load();
    const visible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', visible);
    return () => document.removeEventListener('visibilitychange', visible);
  }, [load]);

  const resolve = async (takeout, choice) => {
    const res = await api.post(`/takeouts/${takeout.id}/resolve`, { choice });
    setOpen((list) => (res.takeout.status === 'awaiting_photo'
      ? list.map((t) => (t.id === takeout.id ? res.takeout : t)) : list.filter((t) => t.id !== takeout.id)));
    if (res.plan) onPlan?.(res.plan);
    if (res.day) onDay?.(res.day);
    if (choice === 'photo' || choice === 'other') capture.open('meal', { date: takeout.date, mealType: takeout.meal_type });
  };
  const added = useCallback((takeout) => setOpen((list) => [...list.filter((t) => t.id !== takeout.id), takeout]), []);
  return { open, resolve, added };
}

function DishCard({ dish, best, opening, apps, onOrder }) {
  return (
    <article className={`flex flex-col gap-2 rounded-[18px] border-2 p-4 ${best ? 'border-primary' : 'border-line'}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[17px] font-bold leading-snug">{dish.name}</h3>
        <span className={`shrink-0 pt-0.5 text-sm font-bold ${FIT_STYLE[dish.fit] || ''}`}>{dish.fit_label}</span>
      </div>
      <p className="tabular text-[15px] text-muted">≈ {kcal(dish.calories)} kcal · <b className="text-protein">{grams(dish.protein_g)} đạm</b></p>
      <SourceLine items={dish.ingredients} className="-mt-1" />
      {dish.tip && (
        <p className="flex items-start gap-2 text-sm"><Icon name="lightbulb" className="mt-0.5 size-4 shrink-0 text-brand-text" /> {dish.tip}</p>
      )}
      <div className="mt-1 grid grid-cols-2 gap-2">
        {apps.map((app) => (
          <button key={app.id} type="button" onClick={() => onOrder(app)} disabled={!!opening}
            aria-label={`Chép "${dish.keyword}" và mở ${app.label}`}
            className={`btn h-11 rounded-[14px] px-2 font-bold ${opening === app.id ? 'btn-primary border-0' : 'border-line bg-base-100'}`}>
            {opening === app.id ? `Đang mở ${app.label}…` : app.label}
          </button>
        ))}
      </div>
    </article>
  );
}

// `request`: { date, mealType, mealPlanId?, dayIndex?, mealIndex?, calories?, protein? } (a planned dish's
// indexes, or the target worked out for an empty slot), or null when closed.
export function TakeoutSheet({ request, onClose, onOrdered }) {
  const { api } = useApp();
  const credits = useAiCredits(1);
  const [takeout, setTakeout] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(null); // `${index}:${app}`
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setTakeout(null);
    setError(null);
    setToast(null);
    setOpening(null);
    if (!request) return;
    api.post('/takeouts', {
      date: request.date, meal_type: request.mealType, meal_plan_id: request.mealPlanId, day_index: request.dayIndex,
      meal_index: request.mealIndex, calories: request.calories, protein_g: request.protein,
    }).then(setTakeout).catch((e) => setError(e.message));
  }, [api, request]);

  // Copy the search words, say so, then open the app. The website opens it at once (a browser blocks a
  // delayed new tab) and keeps the note up a moment; the app pauses so the note can be read first.
  const order = (index, app) => {
    const dish = takeout.suggestions[index];
    const copy = navigator.clipboard?.writeText(dish.keyword);
    setToast({ keyword: dish.keyword, app: app.label, copied: true });
    copy?.catch(() => setToast({ keyword: dish.keyword, app: app.label, copied: false }));
    setOpening(`${index}:${app.id}`);
    api.patch(`/takeouts/${takeout.id}`, { dish: index, app: app.id }).then(onOrdered).catch(() => {});
    const finish = () => {
      setOpening(null);
      onClose();
    };
    if (window.MACRO_MATE_NATIVE) {
      setTimeout(() => { window.open(app.url, '_blank'); finish(); }, 1000);
    } else {
      window.open(app.url, '_blank');
      setTimeout(finish, 2500);
    }
  };

  const more = async () => {
    if (credits.blocked(more)) return;
    setBusy(true);
    setError(null);
    try {
      setTakeout(await api.post(`/takeouts/${takeout.id}/more`, null, { idempotencyKey: credits.key() }));
      credits.used();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const type = request?.mealType;
  const library = takeout?.suggestions.filter((d) => d.source !== 'ai') || [];
  const fromAi = takeout?.suggestions.filter((d) => d.source === 'ai') || [];
  const card = (dish) => {
    const index = takeout.suggestions.indexOf(dish);
    return (
      <DishCard key={`${index}-${dish.name}`} dish={dish} best={index === 0} apps={takeout.apps}
        opening={opening?.startsWith(`${index}:`) ? opening.split(':')[1] : opening && 'other'}
        onOrder={(app) => order(index, app)} />
    );
  };

  return (
    <Sheet open={!!request} onClose={onClose}
      title={type ? `Đặt ngoài ${request.mealPlanId ? 'thay' : 'cho'} ${lower(type)}` : 'Đặt ngoài'}>
      {toast && (
        <div role="status" className="fixed inset-x-4 top-[calc(env(safe-area-inset-top,0px)+12px)] z-50 mx-auto flex max-w-lg items-start gap-3 rounded-[16px] bg-[#17201b] p-4 text-[15px] text-white shadow-xl">
          <Icon name="copy" className="mt-0.5 size-5 shrink-0 text-[#6fd3a0]" />
          <span>
            {toast.copied ? <>Đã chép <b>"{toast.keyword}"</b>. Dán vào ô tìm kiếm của {toast.app}.</>
              : <>Tìm <b>"{toast.keyword}"</b> trên {toast.app}.</>}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {takeout ? (
          <div>
            <p className="tabular text-[17px]">Cần khoảng <b>{kcal(takeout.target_calories)} kcal</b> · <b className="text-protein">{grams(takeout.target_protein_g)} đạm</b></p>
            <p className="text-sm text-muted">Món phổ biến, dễ tìm. Bạn tự chọn quán trên app giao đồ ăn.</p>
          </div>
        ) : !error && (
          <div className="flex flex-col gap-3" aria-label="Đang tải gợi ý">
            {[0, 1, 2].map((i) => <div key={i} className="skeleton h-40 rounded-[18px]" />)}
          </div>
        )}
        {library.map(card)}
        {fromAi.length > 0 && <h3 className="pt-2 text-sm font-bold uppercase tracking-wide text-muted">Gợi ý từ AI</h3>}
        {fromAi.map(card)}
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        {takeout && (
          <div className="flex flex-col items-center gap-1.5 pt-1">
            <button type="button" onClick={more} disabled={busy}
              className="btn h-14 w-full rounded-[16px] border-2 border-dashed border-primary bg-base-100 text-[16px] font-bold text-brand-text">
              {busy ? <><span className="loading loading-spinner loading-sm" /> AI đang tìm món…</> : <><Icon name="sparkles" className="size-5" /> Gợi ý khác bằng AI · 1 lượt</>}
            </button>
            {credits.note && <p className="text-xs text-muted">{credits.note}</p>}
          </div>
        )}
      </div>
    </Sheet>
  );
}

// "Bạn đã đặt món gì?" once back from the delivery app; after "Chụp khi tới", a reminder to photograph it.
export function TakeoutPrompt({ takeout, onResolve }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const run = async (choice) => {
    setBusy(choice);
    setError(null);
    try {
      await onResolve(takeout, choice);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const { dish } = takeout;
  const waiting = takeout.status === 'awaiting_photo';
  const spin = (choice) => busy === choice && <span className="loading loading-spinner loading-sm" />;
  const secondary = 'btn h-12 rounded-[14px] border-line bg-base-100 px-2 font-bold';

  return (
    <section aria-labelledby={`takeout-${takeout.id}`} className="flex flex-col gap-3 rounded-[20px] border-2 border-primary bg-base-100 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id={`takeout-${takeout.id}`} className="text-lg font-bold">{waiting ? 'Đồ ăn tới chưa?' : 'Bạn đã đặt món gì?'}</h2>
          <p className="text-sm text-muted">
            {MEAL_LABELS[takeout.meal_type]} · {waiting ? `${dish.name} trên ${takeout.app_label}` : `vừa tìm "${dish.keyword}" trên ${takeout.app_label}`}
          </p>
        </div>
        <button type="button" aria-label="Không đặt nữa" disabled={!!busy} onClick={() => run('dismissed')}
          className="grid size-10 shrink-0 place-items-center rounded-full text-muted">
          <Icon name="x" className="size-5" />
        </button>
      </div>
      {waiting ? (
        <>
          <button type="button" className="btn btn-primary h-12 rounded-[14px] border-0 font-bold" disabled={!!busy} onClick={() => run('photo')}>
            {spin('photo') || <Icon name="camera" className="size-5" />} Chụp món để ghi
          </button>
          <button type="button" className={secondary} disabled={!!busy} onClick={() => run('logged')}>
            {spin('logged')} Ghi theo ước tính · ≈ {kcal(dish.calories)} kcal
          </button>
        </>
      ) : (
        <>
          <button type="button" className="btn btn-primary h-12 rounded-[14px] border-0 px-3 font-bold" disabled={!!busy} onClick={() => run('logged')}>
            {spin('logged')} <span className="truncate">Ghi "{dish.name}"</span><span className="shrink-0">· ≈ {kcal(dish.calories)} kcal</span>
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className={secondary} disabled={!!busy} onClick={() => run('awaiting_photo')}>
              {spin('awaiting_photo') || <Icon name="camera" className="size-5" />} Chụp khi tới
            </button>
            <button type="button" className={secondary} disabled={!!busy} onClick={() => run('other')}>Món khác</button>
          </div>
        </>
      )}
      <p className="text-xs text-muted">Số liệu là ước tính. Chụp ảnh khi đồ ăn tới để AI tính sát hơn.</p>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
    </section>
  );
}
